/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import swal from 'sweetalert2';
import Loading from '../../components/common/Loading';
import CompanyDetailModal from '../../components/company/CompanyDetailModal';
import CompanyMergeModal from '../../components/company/CompanyMergeModal';

const Container = styled.div`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  height: 94vh;
  overflow: scroll;
  width: 100%;
  margin: 16px 16px 16px 0;
  padding: 28px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.04), 0 4px 20px rgba(0, 0, 0, 0.06);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Toolbar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
  flex-wrap: wrap;
`;

const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchInput = styled.input`
  padding: 7px 14px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  width: 240px;
  outline: none;
  transition: border-color 0.15s;
  &:focus { border-color: #007aff; }
`;

const Btn = styled.button`
  padding: 7px 14px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;
  white-space: nowrap;
  background: ${({ $variant }) =>
    $variant === 'primary' ? '#007aff' :
    $variant === 'danger' ? '#ef4444' :
    $variant === 'success' ? '#1d6f42' : '#f3f4f6'};
  color: ${({ $variant }) =>
    $variant === 'primary' || $variant === 'danger' || $variant === 'success' ? '#fff' : '#374151'};
  &:hover {
    opacity: 0.85;
  }
`;

const TotalText = styled.span`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

const TableWrap = styled.table`
  width: 100%;
  border: none;
  border-collapse: collapse;
  td, th {
    text-align: center;
    border: none;
    padding: 10px 12px;
    font-size: 14px;
  }
  tbody tr {
    height: 48px;
    transition: background 0.1s;
    cursor: pointer;
  }
  tbody tr:hover {
    background: rgba(0, 122, 255, 0.04);
  }
  thead > tr {
    background-color: #2d3748;
    height: 44px;
    border-bottom: none;
  }
  thead th {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $type }) => $type === 'test' ? '#fef3c7' : '#dbeafe'};
  color: ${({ $type }) => $type === 'test' ? '#92400e' : '#1e40af'};
`;

const Empty = styled.div`
  color: #86868b;
  font-size: 15px;
  padding: 80px 0;
  text-align: center;
  width: 100%;
`;

const CompanyListPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [mergeMode, setMergeMode] = useState(false);

  const fetchCompanies = async (query) => {
    try {
      setLoading(true);
      const q = query !== undefined ? query : searchQuery;
      const params = q ? `?search=${encodeURIComponent(q)}` : '';
      const { data } = await axios.get(`/admin/company/list${params}`);
      setCompanies(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies('');
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    fetchCompanies(searchInput);
  };

  const handleSearchReset = () => {
    setSearchInput('');
    setSearchQuery('');
    fetchCompanies('');
  };

  const handleCreate = async () => {
    const { value: formValues } = await swal.fire({
      title: '새 회사 등록',
      html:
        '<input id="swal-name" class="swal2-input" placeholder="대표 회사명 (필수)">' +
        '<input id="swal-official" class="swal2-input" placeholder="공식 법인명 (선택)">',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: '등록',
      cancelButtonText: '취소',
      confirmButtonColor: '#007aff',
      cancelButtonColor: '#a3a3a3',
      preConfirm: () => {
        const name = document.getElementById('swal-name').value.trim();
        if (!name) {
          swal.showValidationMessage('회사명을 입력해주세요.');
          return false;
        }
        return {
          name,
          official_name: document.getElementById('swal-official').value.trim(),
        };
      },
    });
    if (formValues) {
      try {
        await axios.post('/admin/company/create', {
          name: formValues.name,
          official_name: formValues.official_name,
          aliases: [formValues.name],
        });
        swal.fire({ text: '등록 완료', icon: 'success', showConfirmButton: false, timer: 1000 });
        fetchCompanies();
      } catch (err) {
        swal.fire({ text: err.response?.data?.message || '오류 발생', icon: 'error', showConfirmButton: false, timer: 1500 });
      }
    }
  };

  const handleDelete = async (e, companyId, companyName) => {
    e.stopPropagation();
    const result = await swal.fire({
      text: `${companyName}을(를) 삭제하시겠습니까? 소속 회원은 미배정 상태가 됩니다.`,
      icon: 'warning',
      iconColor: '#d33',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#a3a3a3',
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
    });
    if (result.isConfirmed) {
      try {
        await axios.patch(`/admin/company/delete/${companyId}`);
        swal.fire({ text: '삭제 완료', icon: 'success', showConfirmButton: false, timer: 1000 });
        fetchCompanies();
      } catch {
        swal.fire({ text: '오류 발생', icon: 'error', showConfirmButton: false, timer: 1000 });
      }
    }
  };

  return (
    <Container>
      <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>회사 관리</h2>
      </div>

      <Toolbar>
        <ToolGroup>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 6 }}>
            <SearchInput
              placeholder="회사명, 법인명, 별칭 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <Btn $variant="primary" type="submit">검색</Btn>
          </form>
          {searchQuery && <Btn onClick={handleSearchReset}>초기화</Btn>}
        </ToolGroup>
        <ToolGroup>
          <Btn $variant="success" onClick={handleCreate}>+ 회사 등록</Btn>
          <Btn onClick={() => setMergeMode(true)}>회사 병합</Btn>
          <TotalText>총 {companies.length}개</TotalText>
        </ToolGroup>
      </Toolbar>

      {loading ? (
        <Loading />
      ) : companies.length === 0 ? (
        <Empty>{searchQuery ? '검색 결과가 없습니다.' : '등록된 회사가 없습니다.'}</Empty>
      ) : (
        <TableWrap>
          <thead>
            <tr>
              <th>대표명</th>
              <th>법인명</th>
              <th>별칭</th>
              <th>소속 회원수</th>
              <th>구분</th>
              <th>삭제</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((c) => (
              <tr key={c._id} onClick={() => setSelectedId(c._id)}>
                <td style={{ fontWeight: 600 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {c.name}
                    {c.admin_memo && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" title="메모 있음">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    )}
                  </span>
                </td>
                <td style={{ color: '#6b7280' }}>{c.official_name || '-'}</td>
                <td style={{ color: '#9ca3af', fontSize: 12, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.aliases.join(', ')}
                </td>
                <td>{c.user_count}명</td>
                <td>
                  {c.is_test ? <Badge $type="test">테스트</Badge> : <Badge>일반</Badge>}
                </td>
                <td>
                  <Btn $variant="danger" style={{ padding: '4px 10px', fontSize: 12 }}
                    onClick={(e) => handleDelete(e, c._id, c.name)}>
                    삭제
                  </Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}

      {selectedId && (
        <CompanyDetailModal
          companyId={selectedId}
          onClose={() => { setSelectedId(null); fetchCompanies(); }}
        />
      )}
      {mergeMode && (
        <CompanyMergeModal
          companies={companies}
          onClose={() => { setMergeMode(false); fetchCompanies(); }}
        />
      )}
    </Container>
  );
};

export default CompanyListPage;
